import ControlledSearch from 'components/elements/ControlledSearch';
import { Option } from 'components/elements/Search';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { customFetch } from 'utils/fetch';

interface IReservedOrder {
  identifier: string;
  reserved_kg: number;
}

interface IReservedCoffee {
  real_kg: number;
  free_kg: number;
  orders: Array<IReservedOrder>;
}

interface ISampleSourceSelectProps {
  sourcesPath: () => string;
  disabled?: boolean;
  sourcesData?: any;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
}

// PLA-980: la muestra sale primero del café que ninguna orden reclamó. Cuando ese
// excedente no alcanza, el resto se le cobra a las órdenes empezando por la más
// reciente, que es la que se llevó lo que quedaba. Esto calcula ese reparto para
// avisarlo ANTES de guardar, no después.
const chargedToOrders = (
  weightKg: number,
  reserved: IReservedCoffee | null
): Array<{ identifier: string; kg: number }> => {
  if (!reserved || !reserved.orders.length) return [];

  let pending = Math.round((weightKg - reserved.free_kg) * 1000) / 1000;
  if (pending <= 0) return [];

  const charges: Array<{ identifier: string; kg: number }> = [];

  reserved.orders.forEach((order) => {
    if (pending <= 0) return;

    const taken = Math.min(pending, order.reserved_kg);
    if (taken <= 0) return;

    charges.push({ identifier: order.identifier, kg: Math.round(taken * 1000) / 1000 });
    pending = Math.round((pending - taken) * 1000) / 1000;
  });

  return charges;
};

const SampleSourceSelect = ({
  sourcesPath,
  sourcesData,
  disabled,
  method = 'POST',
}: ISampleSourceSelectProps): JSX.Element => {
  const { control, setValue, watch } = useFormContext();
  const [fetching, setFetching] = useState(false);
  const [sources, setSources] = useState<Array<Option>>([]);
  const [reserved, setReserved] = useState<IReservedCoffee | null>(null);

  const path = sourcesPath();
  const stringifiedData = JSON.stringify(sourcesData);
  useEffect(() => {
    if (sources.length > 0) {
      setSources(Array<Option>());
    }
    if (path) {
      setFetching(true);

      customFetch(path, sourcesData, method).then((response) => {
        setValue('sample[source]', null);
        setSources(response.sources);
        setReserved(response.reserved_coffee || null);
        setFetching(false);
      });
    }
  }, [path, stringifiedData]);

  if (!fetching && sources.length == 0 && !disabled) {
    return <span>No Sources available</span>;
  }

  const key = path + stringifiedData;

  // El aviso aplica sólo cuando el peso sale del café del lote/set. Si la muestra
  // se toma DE otra muestra, el lote no pierde un gramo y ninguna orden se entera.
  const source = watch('sample[source]');
  const takenFromCoffee = source === 'lot' || source === 'set';
  const weightKg = (parseFloat(watch('sample[base_weight][value]')) || 0) / 1000;
  const charges = takenFromCoffee ? chargedToOrders(weightKg, reserved) : [];

  return (
    <>
      <ControlledSearch
        label={I18n.translate('samples.create.take_sample_from')}
        dataCy='select_sample_source'
        key={key}
        prompt={I18n.translate('samples.create.take_sample_from_placeholder')}
        options={sources}
        control={control}
        name='sample[source]'
        required
      />
      {charges.length > 0 && (
        <span
          className='text--error'
          style={{ fontWeight: 'bold' }}
          data-cy='sample_charged_to_production_order'
        >
          {charges
            .map((charge) =>
              I18n.translate('samples.create.charged_to_production_order', {
                weight: charge.kg.toFixed(3),
                identifier: charge.identifier,
              })
            )
            .join(' ')}
        </span>
      )}
    </>
  );
};
export default SampleSourceSelect;
